export const ZOOM_SCALES = [0.12, 0.22, 0.38, 0.58, 0.85] as const;

export interface CropRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CropMeta extends CropRect {
  scale?: number;
  focalX?: number;
  focalY?: number;
}

export function clampRect(rect: CropRect, imgW: number, imgH: number): CropRect {
  let { left, top, width, height } = rect;
  width = Math.max(32, Math.min(Math.floor(width), imgW));
  height = Math.max(32, Math.min(Math.floor(height), imgH));
  left = Math.max(0, Math.min(Math.floor(left), imgW - width));
  top = Math.max(0, Math.min(Math.floor(top), imgH - height));
  return { left, top, width, height };
}

export function computeAutoCropRect(
  level: number,
  imgW: number,
  imgH: number,
  focalX = 0.5,
  focalY = 0.5,
): CropRect & { scale: number } {
  const scale = ZOOM_SCALES[level] ?? ZOOM_SCALES[0];
  const cropW = Math.max(32, Math.floor(imgW * scale));
  const cropH = Math.max(32, Math.floor(imgH * scale));
  const cx = Math.floor(imgW * focalX);
  const cy = Math.floor(imgH * focalY);
  const left = Math.max(0, Math.min(imgW - cropW, cx - Math.floor(cropW / 2)));
  const top = Math.max(0, Math.min(imgH - cropH, cy - Math.floor(cropH / 2)));
  return { left, top, width: cropW, height: cropH, scale };
}

export function buildAllLevelRects(
  imgW: number,
  imgH: number,
  focalX: number,
  focalY: number,
  custom: Partial<Record<number, CropRect>>,
): Record<number, CropRect> {
  const out: Record<number, CropRect> = {};
  for (let level = 0; level < 5; level++) {
    out[level] = custom[level]
      ? clampRect(custom[level]!, imgW, imgH)
      : computeAutoCropRect(level, imgW, imgH, focalX, focalY);
  }
  return out;
}

export function rectToPayload(levels: Record<number, CropRect>) {
  return Object.entries(levels).map(([level, rect]) => ({
    level: Number(level),
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  }));
}
